import { Button, DialogActions, DialogContent, DialogTitle, Grid } from "@mui/material";
import { useState } from "react";
import { renderClose } from "../../common/dialog";
import { createEntityStore } from "../../common/entityStore";
import { propBoolField, propNumberField, propTextField } from "../../common/entityStoreForm";
import { propInt, propLength, propLengthMax, propMinValue, propRequired, propValidValue, propValueValidator } from "../../common/entityStoreValidators";
import { createListStore, UseListStore } from "../../common/listStore";
import { InventoryArticleDto, InventoryArticlePackageItemDto } from "./types";
import { EntityStoreSaveButton } from "../../common/SaveButton";
import { map, distinctUntilChanged, combineLatest } from "rxjs";
import shallow from "zustand/shallow";
import { FormSection } from "../../common/FormSection";
import { renderListStore } from "../../common/listStoreUi";
import { keyBy } from "lodash";

export const InventoryArticleDialog = (props: {
    close: () => void;
    article: InventoryArticleEditDto | null;
    useArticlesStore: UseListStore<InventoryArticleDto>;
    onChange(): void;
}) => {
    const [useStore] = useState(() => createEntityStore<InventoryArticleEditDto>({
        data: props.article ?? newInventoryArticle(),
        path: "articles/inventoryArticles",
        id: props.article != null ? props.article.id : null,
        getNewId: p => p.id!,
        onSaved() {
            props.onChange();
            props.close();
        },
        propValidators: [
            propRequired("code"), propLength("code", 5, 5),
            propValueValidator("code", (p, e) =>
                props.useArticlesStore.getState().items.filter(i => i.id !== e.id).find(i => i.code === p) &&
                "Code already exists."),
            propRequired("name"),
            propLengthMax("name", 50),
            propValidValue("wt", "wtFormatted"), propRequired("wt"), propInt("wt"), propMinValue("wt", 0),
            propValueValidator("tarCode", (p, e) =>
                p.length > 0 && p.length !== 8 && p.length !== 10 &&
                "Tariff code must be 8 or 10 characters long."),
            propLength("origCountry", 2, 2),
        ],
    }));

    const { isPost, isPackage } = useStore(p => ({ isPost: p.clientData.isPost, isPackage: p.clientData.packageItems.length > 0 }), shallow);
    const disabled = !isPost;

    const [usePackageItemsStore] = useState(() => createListStore<InventoryArticlePackageItemDto & { articleCode: string; articleName: string; }>({
        data: combineLatest([
            useStore.getState().clientData$.pipe(
                map(p => ({ items: p.data.packageItems, isFetching: p.isFetching })),
                distinctUntilChanged(shallow)),
            props.useArticlesStore.getState().items$.pipe(map(p => ({ items: keyBy(p.items, p => p.id), isFetching: p.isFetching }))),
        ]).pipe(map(([packageItems, articles]) => {
            return {
                items: packageItems.items.map(p => {
                    const article = articles.items[p.inventoryArticleId];
                    return { ...p, articleCode: article?.code ?? "???", articleName: article?.name ?? "???" };
                }),
                isFetching: packageItems.isFetching || articles.isFetching,
            };
        })),
        listProps: [
            { column: "quantity", label: "Quantity", width: 70, type: "number" },
            { column: "articleCode", label: "Code", width: 70 },
            { column: "articleName", label: "Name", flex: 1 },
        ],
        itemsPerPageMax: 5,
    }));

    return <>
        <DialogTitle>{props.article?.id == null ? "New inventory article" : `Edit inventory article`}</DialogTitle>
        {renderClose(props.close)}
        <DialogContent>
            <Grid container columnSpacing={2} alignItems="stretch">
                <Grid item xs={12}>{propTextField(useStore)("code", "Code", { disabled })}</Grid>
                <Grid item xs={12}>{propTextField(useStore)("name", "Name", { disabled })}</Grid>
                <Grid item xs={6}>{propTextField(useStore)("tarCode", "Tariff code", { disabled })}</Grid>
                <Grid item xs={6}>{propNumberField(useStore)("wt", "wtFormatted", "Weight", { unit: "g", disabled })}</Grid>
                <Grid item xs={6}>{propTextField(useStore)("origCountry", "Country of origin", { disabled })}</Grid>
                <Grid item xs={6}>{propBoolField(useStore)("isPost", "Postage fee", { disabled: true })}</Grid>
                <Grid item xs={6}>{propTextField(useStore)("status", "Status", { disabled })}</Grid>
                {!isPost &&<Grid item xs={6}>{propBoolField(useStore)("showOnInvoice", "Show on invoice?", { disabled })}</Grid>}
                {isPackage && <Grid item xs={6}>{propBoolField(useStore)("showPackageContent", "Show package content?", { disabled })}</Grid>}
                {isPackage && <Grid item xs={12}><FormSection label="Package items">
                    {renderListStore(usePackageItemsStore, { hideFooter: true })}
                </FormSection></Grid>}
            </Grid>
        </DialogContent>
        <DialogActions>
            <EntityStoreSaveButton useStore={useStore} />
            <Button onClick={props.close} color="negative" variant="outlined">Close</Button>
        </DialogActions>
    </>;
};

interface InventoryArticleEditDto {
    id: number | null;
    code: string;
    name: string;
    origCountry: string;
    tarCode: string;
    isPost: boolean;
    wt: number;
    wtFormatted?: string;
    status: string;
    packageItems: InventoryArticlePackageItemDto[];
    showOnInvoice: boolean;
    showPackageContent: boolean;
}

const newInventoryArticle: () => InventoryArticleEditDto = () => ({
    id: null,
    code: "",
    name: "",
    origCountry: "",
    tarCode: "",
    isPost: true,
    wt: 0,
    status: "Ongoing",
    packageItems: [],
    showOnInvoice: true,
    showPackageContent: false,
});
